<template>
  <div class="item" :class="{'done': item.done, 'deleting': showMe}" :draggable="grabbed">
    <ModalConfirm :showMe="showMe" @confirmed="modalResponse"/>

    <div class="item-due-date" :class="{late: passedDue}" v-if="item.dueDate">
      <span class="due-label">Due:</span>
      {{ item.dueDate }}
    </div>
    <div class="item-image" v-if="hasImage">
      <img :src="item.imageUrl" alt>
    </div>
    <div class="item-text" :class="{'with-image': hasImage, 'with-due-date': hasDueDate}">
      <!-- GRAB -->
      <div @mousedown="grabbed = true" @mouseleave="removeDraggable" class="actions">
        <span class="item-grab">
          <i class="fas fa-grip-lines-vertical"></i>
        </span>
        <!-- DONE -->
        <i v-if="item.done" class="fas fa-check-square" @click="doneItem"></i>
        <i v-else class="fas fa-square" @click="doneItem"></i>
        <!-- SPECIAL -->
        <i
          v-if="item.special"
          :class="{'special': item.special, 'highlight': true}"
          class="fas fa-star"
          @click="markSpecialItem"
        ></i>
        <i v-else :class="{'special': item.special}" class="fas fa-star" @click="markSpecialItem"></i>
        <!-- EXTERNAL FILE -->
        <i
          class="fas fa-external-link-alt"
          @click="editingExternalLink = !editingExternalLink"
          v-if="!editingExternalLink"
        ></i>
        <span v-if="editingExternalLink" class="external-link-editing">
          <input
            class="external-link-input"
            type="text"
            v-model="localExternalLink"
            @keydown="finishEditingLink"
            placeholder="e.g. https://cool-thing.com"
          >
        </span>
        <!-- DUE DATE -->
        <i class="fas fa-calendar-alt" @click="startEditingDueDate" v-if="!editingDueDate"></i>
        <span v-if="editingDueDate" class="due-date-edit">
          <input
            class="due-date-input"
            type="text"
            v-model="localDueDate"
            @keydown="finishEditingDuedate"
          >
        </span>
        <section class="calendar-wrapper" :id="'calendarwrapper-'+item.item_id"></section>
        <!-- IMAGE -->
        <span class="item-image-edit" v-if="!editingImage" @click="startEditingImage">
          <i class="fas fa-image"></i>
        </span>
        <span class="item-image-editing" v-if="editingImage">
          <input
            type="text"
            v-model="localImageUrl"
            @keydown="finishEditingImage"
            placeholder="e.g. https://cool-image.com/thing.jpg"
          >
        </span>
      </div>
      <div class="name-link">
        <!-- NAME -->
        <span
          class="item-name"
          :class="{done: item.done}"
          @click="startEditingName"
          v-if="!editingName"
        >{{ editedName }}</span>
        <span class="item-name-editing" v-if="editingName">
          <textarea type="textarea" v-model="localName" @keydown="finishEditing"></textarea>
        </span>
        <!-- EXTERNAL URL DISPLAY -->
        <span class="item-external-link-display" v-if="item.externalLink">
          <a :href="item.externalLink" target="_blank">{{item.externalLink | truncate }}</a>
        </span>
      </div>
      <!-- DELETE ITEM -->
      <span class="delete-item">
        <i class="fas fa-trash" @click="deleteItem"></i>
      </span>
    </div>
    <!-- STATUS -->
    <div class="status">
      <span
        @click="pickStatus"
        class="status-item not-started"
        :class="{'current':itemStatus==='not-started'}"
      >Not started</span>
      <span
        @click="pickStatus"
        class="status-item started"
        :class="{'current':  itemStatus === 'started'}"
      >Started</span>
      <span
        @click="pickStatus"
        class="status-item ongoing"
        :class="{'current':  itemStatus === 'ongoing'}"
      >Ongoing</span>
      <span
        @click="pickStatus"
        class="status-item finished"
        :class="{'current':  itemStatus === 'finished'}"
      >Finished</span>
    </div>
    <!-- .item-text -->
  </div>
</template>

<script>
import ModalConfirm from "@/components/ModalConfirm";

import niceDatePicker from "@/libs/nice-date-picker-mod";

export default {
  name: "ListItem",
  components: { ModalConfirm },
  props: ["item"],
  data() {
    return {
      grabbed: false,
      editingName: false,
      localName: null,
      editingImage: false,
      localImageUrl: null,
      editingDueDate: false,
      localDueDate: null,
      editingExternalLink: false,
      localExternalLink: null,
      showMe: false
    };
  },
  computed: {
    editedName() {
      return this.item.name;
    },
    hasImage() {
      return this.item.imageUrl ? true : false;
    },
    hasDueDate() {
      return this.item.dueDate ? true : false;
    },
    passedDue() {
      return new Date(this.item.dueDate) < new Date();
    },
    hasExternalLink() {
      return this.item.externalLink ? true : false;
    },
    itemStatus() {
      return this.item.status ? this.item.status : "not-started";
    }
  },
  watch: {
    editingName() {
      this.localName = this.localName || this.editedName;
    },
    editingImage() {
      this.localImageUrl = this.localImageUrl || this.item.imageUrl;
    },
    editingDueDate() {
      this.localDueDate = this.item.dueDate
        ? this.item.dueDate.toLocaleString()
        : new Date()
            .toLocaleString()
            .split(" ")[0]
            .replace(",", "");
    },
    editingExternalLink() {
      this.localExternalLink = this.localExternalLink || this.item.externalLink;
    }
  },
  methods: {
    deleteItem() {
      this.showMe = true;
    },

    modalResponse(response) {
      if (response) {
        this.$emit("deleteMe", this.item);
      }
      this.showMe = false;
    },

    removeDraggable() {
      this.grabbed = false;
    },

    startEditingName() {
      this.editingName = !this.editingName;
    },

    startEditingImage() {
      this.editingImage = !this.editingImage;
    },

    finishEditing(e) {
      if (e.which === 27) {
        this.editingName = false;
      }
      if (e.which === 13) {
        this.editingName = false;
        this.$emit("updateMe", { newName: this.localName, item: this.item });
        this.localName = null;
      }
    },

    finishEditingImage(e) {
      if (e.which === 27) {
        this.editingImage = false;
      }
      if (e.which === 13) {
        this.editingImage = false;
        this.$emit("updateMeImage", {
          newImageUrl: this.localImageUrl,
          item: this.item
        });
        this.localName = null;
      }
    },

    doneItem() {
      this.item.done = !this.item.done;
      if (this.item.done) {
        this.item.status = "finished";
      }
      this.$emit("doneItemToggle", { item: this.item });
    },

    markSpecialItem() {
      this.item.special = !this.item.special;
      this.$emit("specialItemToggle", { item: this.item });
    },

    closeCalendar() {
      const elementId = "calendarwrapper-" + this.item.item_id;
      const el = document.getElementById(elementId);
      const target = el.firstChild;
      target.style.display = "none";
    },

    startEditingDueDate() {
      const id = "calendarwrapper-" + this.item.item_id;
      new niceDatePicker({
        dom: document.getElementById(id),
        mode: "en",
        onClickDate: date => {
          this.localDueDate = date.toLocaleString();
        }
      });
      this.editingDueDate = !this.editingDueDate;
    },

    finishEditingDuedate(e) {
      if (e.which === 27) {
        this.editingDueDate = false;
        this.localDueDate = null;
      }
      if (e.which === 13) {
        this.editingDueDate = false;
        const newDueDate = this.localDueDate;
        this.$emit("updateMeDueDate", {
          newDueDate: newDueDate,
          item: this.item
        });
        this.localDueDate = null;
      }
      this.closeCalendar(e);
    },

    finishEditingLink(e) {
      if (e.which === 27) {
        this.editingExternalLink = false;
      }
      if (e.which === 13) {
        this.editingExternalLink = false;
        const newExternalLink = this.localExternalLink;
        this.$emit("updateMeExternalLink", {
          newExternalLink: newExternalLink,
          item: this.item
        });
        this.localExternalLink = null;
      }
    },

    pickStatus(e) {
      const choice = e.target.classList[1];
      this.$emit("setStatus", { item: this.item, choice: choice });
    }
  }
};
</script>
